// Persistent resume for single-player / shared-screen arcade games.
//
// If a player exits a game (navigates away, closes the app), they can jump back
// in and continue from the EXACT spot for up to 24 hours. Rules:
//   • Stored in localStorage → survives closing the tab/app (cross-session).
//   • Stamped with a timestamp → discarded once older than RESUME_TTL_MS (24h).
//   • Stamped with the userId → switching accounts discards it.
//   • Cleared on logout (see clearAllGameSaves, called from App logout).
//
// Games opt in per-game because each game's state is bespoke (see SudokuPage
// for the reference pattern). Set() values survive round-trips via the custom
// replacer/reviver below.

const PREFIX = "iw_game_save_v1:";
const RESUME_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const now = () => Date.now();
const fresh = (savedAt) => typeof savedAt === "number" && (now() - savedAt) < RESUME_TTL_MS;

const replacer = (_k, v) => (v instanceof Set ? { __set: [...v] } : v);
const reviver = (_k, v) => (v && typeof v === "object" && Array.isArray(v.__set) ? new Set(v.__set) : v);

export function loadGameSnapshot(gameKey, userId = null) {
  try {
    const raw = localStorage.getItem(PREFIX + gameKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw, reviver);
    if (!fresh(parsed.savedAt)) { localStorage.removeItem(PREFIX + gameKey); return null; }
    if (userId && parsed.userId && parsed.userId !== userId) { localStorage.removeItem(PREFIX + gameKey); return null; }
    return parsed.state ?? null;
  } catch {
    return null;
  }
}

export function saveGameSnapshot(gameKey, state, userId = null) {
  try {
    localStorage.setItem(
      PREFIX + gameKey,
      JSON.stringify({ savedAt: now(), userId: userId || null, state }, replacer)
    );
  } catch {
    /* storage full / unavailable — resume is a best-effort nicety */
  }
}

export function clearGameSnapshot(gameKey) {
  try { localStorage.removeItem(PREFIX + gameKey); } catch { /* noop */ }
}

// True if there's a fresh (<24h) solo save for this game (used for arcade tile badges).
export function hasGameSaveToday(gameKey, userId = null) {
  return loadGameSnapshot(gameKey, userId) !== null;
}

// Set of gameKeys (the part before ":solo") that have a valid fresh solo save.
export function getSavedSoloGamesToday() {
  const out = new Set();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX) && k.endsWith(":solo")) {
        try {
          const parsed = JSON.parse(localStorage.getItem(k));
          if (parsed && fresh(parsed.savedAt)) out.add(k.slice(PREFIX.length, k.length - 5));
        } catch { /* skip */ }
      }
    }
  } catch { /* noop */ }
  return out;
}

export function clearAllGameSaves() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* noop */
  }
}
